(function () {
  'use strict';

  // ── Vertex shader (shared) ───────────────────────────────────────────────
  const VERT = `
    attribute vec2 a_position;
    varying vec2 vUv;
    void main() {
      vUv = a_position * 0.5 + 0.5;
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `;

  // ── Wave fragment shader ─────────────────────────────────────────────────
  const WAVE_FRAG = `
    precision highp float;
    uniform vec2  u_resolution;
    uniform float u_time;
    uniform float u_waveSpeed;
    uniform float u_waveFrequency;
    uniform float u_waveAmplitude;
    uniform vec3  u_waveColor;
    uniform vec2  u_mousePos;
    uniform int   u_enableMouse;
    uniform float u_mouseRadius;
    varying vec2  vUv;

    vec4 mod289v(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
    vec4 permute(vec4 x){return mod289v(((x*34.0)+1.0)*x);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
    vec2 fade2(vec2 t){return t*t*t*(t*(t*6.0-15.0)+10.0);}

    float cnoise(vec2 P){
      vec4 Pi=floor(P.xyxy)+vec4(0,0,1,1);
      vec4 Pf=fract(P.xyxy)-vec4(0,0,1,1);
      Pi=mod289v(Pi);
      vec4 ix=Pi.xzxz,iy=Pi.yyww,fx=Pf.xzxz,fy=Pf.yyww;
      vec4 i=permute(permute(ix)+iy);
      vec4 gx=fract(i*(1.0/41.0))*2.0-1.0;
      vec4 gy=abs(gx)-0.5;
      vec4 tx=floor(gx+0.5);gx=gx-tx;
      vec2 g00=vec2(gx.x,gy.x),g10=vec2(gx.y,gy.y),g01=vec2(gx.z,gy.z),g11=vec2(gx.w,gy.w);
      vec4 norm=taylorInvSqrt(vec4(dot(g00,g00),dot(g01,g01),dot(g10,g10),dot(g11,g11)));
      g00*=norm.x;g01*=norm.y;g10*=norm.z;g11*=norm.w;
      float n00=dot(g00,vec2(fx.x,fy.x));
      float n10=dot(g10,vec2(fx.y,fy.y));
      float n01=dot(g01,vec2(fx.z,fy.z));
      float n11=dot(g11,vec2(fx.w,fy.w));
      vec2 fade_xy=fade2(Pf.xy);
      vec2 n_x=mix(vec2(n00,n01),vec2(n10,n11),fade_xy.x);
      return 2.3*mix(n_x.x,n_x.y,fade_xy.y);
    }

    float fbm(vec2 p){
      float v=0.0,amp=1.0,freq=u_waveFrequency;
      for(int i=0;i<4;i++){v+=amp*abs(cnoise(p));p*=freq;amp*=u_waveAmplitude;}
      return v;
    }

    float pattern(vec2 p){
      vec2 p2=p-u_time*u_waveSpeed;
      return fbm(p+fbm(p2));
    }

    void main() {
      // In original React code, uv is centered:
      vec2 uv = vUv - 0.5;
      uv.x *= u_resolution.x / u_resolution.y;
      
      float f = pattern(uv);

      // Mouse
      if (u_enableMouse == 1) {
        vec2 mouseNDC = (u_mousePos / u_resolution - 0.5) * vec2(1.0, -1.0);
        mouseNDC.x *= u_resolution.x / u_resolution.y;
        float dist = length(uv - mouseNDC); // FIXED: use uv instead of vUv
        float effect = 1.0 - smoothstep(0.0, u_mouseRadius, dist);
        f -= 0.5 * effect;
      }

      // Convert sRGB color to linear space like Three.js does
      vec3 linearWaveColor = pow(u_waveColor, vec3(2.2));
      vec3 col = mix(vec3(0.0), linearWaveColor, f);
      
      gl_FragColor = vec4(col, 1.0);
    }
  `;

  // ── Dither post-process fragment shader (WebGL1 / GLSL ES 1.00 compatible) ─
  const DITHER_FRAG = `
    precision highp float;
    uniform sampler2D u_tex;
    uniform vec2  u_resolution;
    uniform float u_colorNum;
    uniform float u_pixelSize;
    varying vec2 vUv;

    float bayer8(vec2 p) {
      p = mod(floor(p), 8.0);
      float b = 0.0;
      float s = 1.0;
      float px = p.x; float py = p.y;
      for (int i = 0; i < 3; i++) {
        float rx = mod(px, 2.0);
        float ry = mod(py, 2.0);
        b += s * (ry * 4.0 + rx * (1.0 - 2.0 * ry) + ry * (2.0 * rx - 1.0));
        px = floor(px / 2.0);
        py = floor(py / 2.0);
        s *= 4.0;
      }
      return b / 64.0;
    }

    vec3 ditherColor(vec2 fragCoord, vec3 color) {
      vec2 sc = floor(fragCoord / u_pixelSize);
      float threshold = bayer8(sc) - 0.25;
      float stp = 1.0 / (u_colorNum - 1.0);
      color += threshold * stp;
      color = clamp(color - 0.2, 0.0, 1.0);
      return floor(color * (u_colorNum - 1.0) + 0.5) / (u_colorNum - 1.0);
    }

    void main() {
      vec2 normPS = u_pixelSize / u_resolution;
      vec2 uvPixel = normPS * floor(vUv / normPS);
      vec3 col = texture2D(u_tex, uvPixel).rgb;
      col = ditherColor(gl_FragCoord.xy, col);
      
      // Convert linear back to sRGB for display!
      col = pow(col, vec3(1.0 / 2.2));
      
      gl_FragColor = vec4(col, 1.0);
    }
  `;

  // ── Helpers ──────────────────────────────────────────────────────────────
  function compileShader(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn('[dither] shader error:', gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  function buildProgram(gl, vertSrc, fragSrc) {
    const v = compileShader(gl, gl.VERTEX_SHADER, vertSrc);
    const f = compileShader(gl, gl.FRAGMENT_SHADER, fragSrc);
    if (!v || !f) return null;
    const p = gl.createProgram();
    gl.attachShader(p, v); gl.attachShader(p, f);
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.warn('[dither] link error:', gl.getProgramInfoLog(p));
      return null;
    }
    return p;
  }

  function createQuad(gl) {
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
    return buf;
  }

  function bindQuad(gl, prog, buf) {
    const loc = gl.getAttribLocation(prog, 'a_position');
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  }

  // ── Main init ────────────────────────────────────────────────────────────
  function init() {
    const canvas = document.getElementById('dither-bg');
    if (!canvas) return;

    // Style canvas as full-viewport fixed background
    Object.assign(canvas.style, {
      position: 'fixed', top: '0', left: '0',
      width: '100vw', height: '100vh',
      zIndex: '-1', pointerEvents: 'none', display: 'block'
    });

    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    if (!gl) {
      // Graceful fallback — CSS background already #050505
      canvas.style.display = 'none';
      return;
    }

    // Check for GLSL array support (WebGL1 fallback needs adjustment)
    const isWebGL2 = gl instanceof WebGL2RenderingContext;

    const waveProg   = buildProgram(gl, VERT, WAVE_FRAG);
    const ditherProg = buildProgram(gl, VERT, DITHER_FRAG);
    if (!waveProg || !ditherProg) return;

    const quad = createQuad(gl);

    // Wave uniforms
    const uRes   = gl.getUniformLocation(waveProg, 'u_resolution');
    const uTime  = gl.getUniformLocation(waveProg, 'u_time');
    const uSpeed = gl.getUniformLocation(waveProg, 'u_waveSpeed');
    const uFreq  = gl.getUniformLocation(waveProg, 'u_waveFrequency');
    const uAmp   = gl.getUniformLocation(waveProg, 'u_waveAmplitude');
    const uColor = gl.getUniformLocation(waveProg, 'u_waveColor');
    const uMouse = gl.getUniformLocation(waveProg, 'u_mousePos');
    const uEnM   = gl.getUniformLocation(waveProg, 'u_enableMouse');
    const uMRad  = gl.getUniformLocation(waveProg, 'u_mouseRadius');

    // Dither uniforms
    const dRes   = gl.getUniformLocation(ditherProg, 'u_resolution');
    const dTex   = gl.getUniformLocation(ditherProg, 'u_tex');
    const dCNum  = gl.getUniformLocation(ditherProg, 'u_colorNum');
    const dPSize = gl.getUniformLocation(ditherProg, 'u_pixelSize');

    // Framebuffer + texture for wave → dither pass
    let fb, fbTex;

    function createFB(w, h) {
      if (fb) { gl.deleteFramebuffer(fb); gl.deleteTexture(fbTex); }
      fbTex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, fbTex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, fbTex, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    let W = 0, H = 0;
    function resize() {
      const w = window.innerWidth, h = window.innerHeight;
      if (w === W && h === H) return;
      W = w; H = h;
      canvas.width = W; canvas.height = H;
      createFB(W, H);
    }
    resize();

    // Mouse
    let mx = 0, my = 0;
    window.addEventListener('mousemove', e => { mx = e.clientX; my = e.clientY; });

    // Animation params (matching Dither.tsx defaults)
    const WAVE_COLOR   = [0.23529, 0.34510, 0.23529]; // #3C583C-ish (same as original)
    const WAVE_SPEED   = 0.02;
    const WAVE_FREQ    = 5.0;
    const WAVE_AMP     = 0.25;
    const COLOR_NUM    = 4.0;
    const PIXEL_SIZE   = 2.0;
    const MOUSE_RADIUS = 0.9;

    let startTime = performance.now();
    let raf;

    function render() {
      resize();

      const t = (performance.now() - startTime) / 1000;

      // Pass 1: render wave to framebuffer
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
      gl.viewport(0, 0, W, H);
      gl.useProgram(waveProg);
      bindQuad(gl, waveProg, quad);
      gl.uniform2f(uRes, W, H);
      gl.uniform1f(uTime, t);
      gl.uniform1f(uSpeed, WAVE_SPEED);
      gl.uniform1f(uFreq, WAVE_FREQ);
      gl.uniform1f(uAmp, WAVE_AMP);
      gl.uniform3fv(uColor, WAVE_COLOR);
      gl.uniform2f(uMouse, mx, my);
      gl.uniform1i(uEnM, 1);
      gl.uniform1f(uMRad, MOUSE_RADIUS);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      // Pass 2: dither from framebuffer to screen
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, W, H);
      gl.useProgram(ditherProg);
      bindQuad(gl, ditherProg, quad);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, fbTex);
      gl.uniform1i(dTex, 0);
      gl.uniform2f(dRes, W, H);
      gl.uniform1f(dCNum, COLOR_NUM);
      gl.uniform1f(dPSize, PIXEL_SIZE);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

      raf = requestAnimationFrame(render);
    }

    render();

    // Pause when tab is hidden (battery/performance saving)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { cancelAnimationFrame(raf); }
      else { startTime = performance.now() - startTime; render(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
